import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionField, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectArguments, selectValues } from "../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import { isCustomColumn, isDimensionBased, isMeasure } from "../../../../utilities/fieldsHelper.ts";
import DimensionFormattingContainer from "../../common/fields/format/DimensionFormattingContainer.tsx";
import { canApplyFormat } from "../../common/fields/format/dimensionFormattingHelper.tsx";
import MeasureFieldsContainer from "../../common/fields/format/MeasureFieldsContainer.tsx";
import TextFieldContainer from "./TextFieldsContainer.tsx";

export default function FormatContainer({ readonlyMode }: { readonlyMode: boolean }) {
  const argumentsArea = useSelector(selectArguments);
  const valuesArea = useSelector(selectValues);
  const dispatch = useAppDispatch();

  const supportingFormattingMeasures = useMemo(
    () => valuesArea.filter((f) => isMeasure(f) || isCustomColumn(f)),
    [valuesArea]
  );

  const supportingFormattingDimensions = useMemo(
    () => valuesArea.filter(isDimensionBased).filter(canApplyFormat),
    [valuesArea]
  );

  const updateMeasureItemConfig = useCallback(
    (field: ReportField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateMeasureConfig({ field, changes }));
    },
    [dispatch]
  );

  const updateArgumentItemConfig = useCallback(
    (field: DimensionField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateArgumentConfig({ field, changes }));
    },
    [dispatch]
  );

  const handleMeasureChanged = useCallback(
    (measure: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = valuesArea.find((f) => f.config.guid === measure.config.guid);
      if (field !== undefined) {
        updateMeasureItemConfig(field, changes);
      }
    },
    [updateMeasureItemConfig, valuesArea]
  );

  const handleDimensionChange = useCallback(
    (dimension: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = valuesArea.find((f) => f.config.guid === dimension.config.guid);
      if (isDimensionBased(field)) {
        updateMeasureItemConfig(field, changes);
      }
    },
    [updateMeasureItemConfig, valuesArea]
  );

  return (
    <ScrollableFlexContainer>
      <Stack sx={{ gap: 1, mx: "1.3rem" }}>
        <TextFieldContainer
          fields={argumentsArea}
          onUpdateConfig={updateArgumentItemConfig}
          readonlyMode={readonlyMode}
        />
        <DimensionFormattingContainer
          dimensions={supportingFormattingDimensions}
          onUpdateConfig={handleDimensionChange}
          readonlyMode={readonlyMode}
        />
        <MeasureFieldsContainer
          measures={supportingFormattingMeasures}
          onChange={handleMeasureChanged}
          readonlyMode={readonlyMode}
        />
      </Stack>
    </ScrollableFlexContainer>
  );
}
